import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import { api, apiEndpoints, getCurrentStoreId } from '../utils/apiService';

export type GuestTag = 'vip' | 'repeat' | 'new';

export interface CustomerRow {
  customer_id: number;
  name: string | null;
  email: string | null;
  phone: string | null;
  total_spent: number;
  total_orders: number;
  opentable_reservations: number;
  last_contact: string | null;
  customer_since: string;
  is_vip?: boolean;
  vip_name?: string | null;
  /** From the API. Absent responses fall back to VIP, then visit counts. */
  guest_tag?: GuestTag | null;
}

const GUEST_TAGS: readonly GuestTag[] = ['vip', 'repeat', 'new'];

const isGuestTag = (value: unknown): value is GuestTag =>
  typeof value === 'string' &&
  (GUEST_TAGS as readonly string[]).includes(value);

/**
 * Guest chip. The API's `guest_tag` wins. Without it: VIP, then more than
 * one visit (paid orders + reservations) is a repeat guest, otherwise new.
 */
export function resolveGuestTag(customer: {
  guest_tag?: GuestTag | null;
  is_vip?: boolean;
  total_orders?: number;
  opentable_reservations?: number;
}): GuestTag {
  if (isGuestTag(customer.guest_tag)) return customer.guest_tag;
  if (customer.is_vip) return 'vip';
  const visits =
    (customer.total_orders || 0) + (customer.opentable_reservations || 0);
  return visits > 1 ? 'repeat' : 'new';
}

export interface CustomersQueryResult {
  customers: CustomerRow[];
  totalCount: number;
}

async function fetchCustomersPage(
  page: number,
  pageSize: number,
  ordering?: string,
  search?: string,
): Promise<CustomersQueryResult> {
  const response = await api.get(
    apiEndpoints.getCustomers(page, pageSize, ordering, {
      search: search || undefined,
    }),
  );
  if (!response?.ok) {
    throw new Error('Failed to fetch customers');
  }
  const data = await response.json();
  let customersList: CustomerRow[] = [];

  if (data.results && Array.isArray(data.results)) {
    customersList = data.results.map((customer: Record<string, unknown>) => ({
      customer_id: customer.id as number,
      name: (customer.name as string | null) ?? null,
      email: (customer.email as string | null) ?? null,
      phone: (customer.phone as string | null) ?? null,
      total_spent: (customer.total_spent as number) ?? 0,
      total_orders:
        (customer.total_orders as number) ??
        (customer.number_of_orders as number) ??
        0,
      opentable_reservations: (customer.opentable_reservations as number) ?? 0,
      last_contact:
        (customer.last_contact as string) ??
        (customer.last_contact_at as string) ??
        (customer.last_interaction_at as string) ??
        (customer.last_order_at as string) ??
        null,
      customer_since: customer.created_at as string,
      is_vip: Boolean(customer.is_vip),
      vip_name: (customer.vip_name as string | null) ?? null,
      guest_tag: isGuestTag(customer.guest_tag) ? customer.guest_tag : null,
    }));
    return {
      customers: customersList,
      totalCount: (data.count as number) || 0,
    };
  }

  if (Array.isArray(data)) {
    customersList = data.map((customer: Record<string, unknown>) => ({
      customer_id: customer.id as number,
      name: (customer.name as string | null) ?? null,
      email: (customer.email as string | null) ?? null,
      phone: (customer.phone as string | null) ?? null,
      total_spent: (customer.total_spent as number) ?? 0,
      total_orders:
        (customer.total_orders as number) ??
        (customer.number_of_orders as number) ??
        0,
      opentable_reservations: (customer.opentable_reservations as number) ?? 0,
      last_contact:
        (customer.last_contact as string) ??
        (customer.last_contact_at as string) ??
        (customer.last_interaction_at as string) ??
        (customer.last_order_at as string) ??
        null,
      customer_since: customer.created_at as string,
      is_vip: Boolean(customer.is_vip),
      vip_name: (customer.vip_name as string | null) ?? null,
      guest_tag: isGuestTag(customer.guest_tag) ? customer.guest_tag : null,
    }));
    return { customers: customersList, totalCount: data.length };
  }

  console.error('Unexpected response format:', data);
  return { customers: [], totalCount: 0 };
}

export function useCustomersQuery(
  page: number,
  pageSize: number,
  enabled: boolean,
  ordering?: string,
  search?: string,
) {
  const { user } = useAuth();
  const currentStoreId = getCurrentStoreId();

  return useQuery({
    queryKey: [
      'customers',
      user?.id ?? 'anonymous',
      currentStoreId,
      page,
      pageSize,
      ordering ?? '',
      search ?? '',
    ],
    queryFn: () => fetchCustomersPage(page, pageSize, ordering, search),
    enabled,
    placeholderData: keepPreviousData,
  });
}
